import React from 'react'
import PropTypes from 'prop-types'
import { api } from 'components/api'
import SearchFilters from './SearchFilters'
import SearchForm from './SearchForm'
import SearchResults from './SearchResults'
import LoadingDots from '../LoadingDots'
import { commodityCodePath } from '../routes/api'
import { supplierSearchPath } from '../routes/path'
import ServiceAreaSelector from './ServiceAreaSelector'
import SearchSummary from './SearchSummary'
import { isTopInViewport } from '../utils'
import { StatesContext } from './StatesContext'
import { forOwn, isArray, isString } from 'lodash-es'
import { SupplierResultShape } from './prop_shapes/SupplierResultShape'
import { createContentVisit } from '../createContentVisit'
import { CategoryAd } from './CategoryAd'

class Page extends React.Component {
  constructor (props) {
    super(props)
    this.mobileFilterModalRef = React.createRef()
    this.searchPath = this.props.category ? `/${this.props.category.slug}.js` : `${supplierSearchPath()}.js`

    const hasActiveSegment = this.props.filterResults.active_segment
    this.state = {
      searchId: props.searchId,
      searchTerm: props.queryParams.search,
      searchState: props.queryParams.state,
      searchResults: props.searchResults,
      filterResults: props.filterResults,
      page: props.searchResults.current_page,
      selectedCommodityCodeSegmentId: hasActiveSegment
        ? hasActiveSegment.id
        : null,
      selectedCommodityCodeSegmentTitle: hasActiveSegment
        ? hasActiveSegment.title
        : null,
      activeCommodityFamilies: [],
      searching: false,
      categoryIds: this.initialSelectedCategoryIds(),
      designations: this.initialSelectedDesignations(),
      seg: this.initialCommodityCodeSegment(),
      commodityCodes: [],
      minRating: this.initialMinRating()
    }
  }

  initialSelectedCategoryIds = () => {
    let selectedCategoryIds = []
    const param = this.props.queryParams.category_ids
    if (param) {
      if (isString(param)) {
        selectedCategoryIds = param.split('|').reduce((result, category) => {
          result.push(parseInt(category, 10))
          return result
        }, [])
      } else if (isArray(param)) {
        return param.map((category) => parseInt(category, 10))
      } else {
        return []
      }
    }
    return [...new Set(selectedCategoryIds)]
  }

  initialSelectedDesignations = () => {
    let selectedDesignations = []
    if (this.props.queryParams.designations) {
      selectedDesignations = this.props.queryParams.designations
        .reduce((result, designationName) => {
          const designation = this.props.filterResults.all_designations.find(
            (d) => d.name === designationName
          )
          if (designation) {
            result.push(designation.name)
          }
          return result
        }, [])
    }

    return selectedDesignations
  }

  initialCommodityCodeSegment = () => {
    if (this.props.queryParams.seg) {
      return this.props.queryParams.seg
    }

    return null
  }

  initialMinRating = () => {
    if (this.props.queryParams.min_rating) {
      return parseInt(this.props.queryParams.min_rating)
    } else {
      return 0
    }
  }

  componentDidMount () {
    this.updateCommodityCodes()
    this.trackSponsoredSearchResult()
  }

  updateCommodityCodes () {
    if (this.state.selectedCommodityCodeSegmentId) {
      api.get({
        url: commodityCodePath(this.state.selectedCommodityCodeSegmentId),
        headers: {
          Accept: 'application/json'
        }
      }).then((response) => {
        this.setState({
          activeCommodityFamilies: response.data,
          commodityCodes: this.initialSelectedCommodityCodes(response.data)
        })
      })
    }
  }

  initialSelectedCommodityCodes = (activeCommodityFamilies) => {
    let selectedCommodityCodes = []
    if (this.props.queryParams.commodity_codes) {
      selectedCommodityCodes = this.props.queryParams.commodity_codes
        .split('|')
        .reduce((result, commodityCodeDescription) => {
          const commodityCode = activeCommodityFamilies.find(
            (c) =>
              c.item_description.toLowerCase() ===
              commodityCodeDescription.toLowerCase()
          )
          if (commodityCode) {
            result.push(commodityCode.item_description)
          }
          return result
        }, [])
    }

    return selectedCommodityCodes
  }

  componentDidUpdate (prevProps, prevState) {
    if (this.state.page !== prevState.page ||
      (this.currentCategory() && this.currentCategory().id !== prevState.categoryIds[0])) {
      this.trackSponsoredSearchResult()
    }
  }

  currentCategory = () => {
    return this.props.filterResults.all_categories.find(c => c.id === this.state.categoryIds[0])
  }

  trackSponsoredSearchResult () {
    const sponsoredResult = this.sponsoredSearchResult()
    if (sponsoredResult) {
      const sponsoredSupplierId = Number(sponsoredResult.id)
      const sponsoredSupplierCategoryId = this.currentCategory().id
      this.createSponsoredSearchResultContentVisit(sponsoredSupplierId, sponsoredSupplierCategoryId)
      this.createSponsoredSearchResultGtmEvent(sponsoredSupplierId, sponsoredSupplierCategoryId)
    }
  }

  sponsoredSearchResult = () => {
    if (this.state.page !== 1) {
      return null
    }
    const currentCategory = this.currentCategory()
    if (!currentCategory) {
      return null
    }
    const sponsoredResults = this.props.sponsoredSearchResults[currentCategory.id]
    if (!sponsoredResults) {
      return null
    }
    return sponsoredResults[Math.floor(Math.random() * sponsoredResults.length)]
  }

  createSponsoredSearchResultContentVisit (sponsoredSupplierId, sponsoredSupplierCategoryId) {
    createContentVisit({
      event_type: 'sponsored_search_result_view',
      data: {
        sponsored_supplier_id: sponsoredSupplierId,
        sponsored_supplier_category_id: sponsoredSupplierCategoryId
      }
    })
  }

  createSponsoredSearchResultGtmEvent (sponsoredSupplierId, sponsoredSupplierCategoryId) {
    window.dataLayer.push({
      event: 'sponsoredSearchResultView',
      sponsoredSupplierId,
      sponsoredSupplierCategoryId
    })
  }

  searchParams = (page) => {
    return {
      search: this.state.searchTerm,
      state: this.state.searchState,
      page: page || 1,
      min_rating: this.state.minRating,
      designations: this.state.designations.join('|'),
      commodity_codes: this.state.commodityCodes.join('|'),
      seg: this.state.seg,
      category_ids: (this.state.categoryIds || []).join('|')
    }
  }

  filterCount = () => {
    let count = 0
    const searchParams = this.searchParams()

    const paramsToRemove = ['page', 'state', 'search']
    forOwn(searchParams, (value, key) => {
      if (paramsToRemove.includes(key) || !value) {
        delete searchParams[key]
      }
      if (
        searchParams[key] &&
        isString(searchParams[key]) &&
        searchParams[key].includes('|')
      ) {
        count = count + searchParams[key].split('|').length
      } else if (isString(searchParams[key])) {
        count = count + 1
      }
    })

    if (searchParams.min_rating) {
      count = count + 1
    }

    return count
  }

  executeNewSearch = (newSearch) => {
    let searchTerm = this.state.searchTerm
    let searchState = this.state.searchState

    if (newSearch.searchTerm !== undefined) {
      searchTerm = newSearch.searchTerm
    } else if (newSearch.searchState !== undefined) {
      searchState = newSearch.searchState
    }

    this.setState(
      {
        searchTerm,
        searchState,
        selectedCommodityCodeSegmentId: null,
        selectedCommodityCodeSegmentTitle: null,
        categoryIds: [],
        designations: [],
        seg: null,
        commodityCodes: [],
        minRating: 0
      },
      this.executeSearch
    )
  }

  executeSearch = (page) => {
    this.setState({ searching: true })
    this.scrollToTop()

    page = page || 1

    const params = this.searchParams(page)
    params.entry_point = 'results'

    api.get({
      url: this.searchPath,
      headers: {
        Accept: 'application/json'
      },
      params
    }).then((response) => {
      this.setState({
        searchResults: response.data.search_results,
        filterResults: response.data.filter_results,
        searchId: response.data.search_id,
        page,
        searching: false
      })

      const url = new URL(window.location)
      forOwn(this.searchParams(), (value, key) => {
        if (value) {
          url.searchParams.set(key, value)
        } else {
          url.searchParams.delete(key)
        }
      })

      window.history.pushState({}, '', url)
    })
  }

  scrollToTop = () => {
    const $mainContainer = $('.main-container')
    if (!isTopInViewport($mainContainer[0])) {
      let scrollTopPosition = $mainContainer.offset().top
      const $topNav = $('.top-nav')
      if ($topNav.length > 0) {
        scrollTopPosition -= $topNav.outerHeight(true)
      }
      $('html, body').animate({ scrollTop: scrollTopPosition }, 'slow')
    }
  }

  handleOpenMobileFilters = () => {
    $(this.mobileFilterModalRef.current).foundation('open')
  }

  handleCloseMobileFilter = () => {
    $(this.mobileFilterModalRef.current).foundation('close')
  }

  handleFilterClear = () => {
    const categoryIds = []
    if (this.props.category) {
      categoryIds.push(this.props.category.id)
    }

    this.setState(
      {
        selectedCommodityCodeSegmentId: null,
        selectedCommodityCodeSegmentTitle: null,
        selectedCategoryIds: [],
        selectedCategories: [],
        categoryIds,
        designations: [],
        seg: null,
        commodityCodes: [],
        minRating: 0
      },
      this.executeSearch
    )
  }

  handleCategoriesChange = categoryIds => {
    this.setState({ categoryIds }, this.executeSearch)
  }

  handleDesignationsChange = designations => {
    this.setState({ designations }, this.executeSearch)
  }

  handleSegChange = seg => {
    this.setState({ seg }, this.executeSearch)
  }

  handleCommodityCodeChange = commodityCodes => {
    this.setState({ commodityCodes }, this.executeSearch)
  }

  handleCommodityCodeSegmentChange = (stateChange, skipSearch) => {
    if (skipSearch) {
      this.setState(stateChange)
    } else {
      this.setState(stateChange, this.executeSearch)
    }
  }

  handleMinRatingChange = minRating => {
    this.setState({ minRating }, this.executeSearch)
  }

  handleLockedClick = (event, redirectTo) => {
    if (this.props.locked) {
      event.preventDefault()
      if (!this.props.signedIn) {
        localStorage.setItem('redirect_to', redirectTo)
        $('#locked-modal').foundation('open')
      }
    }
  }

  renderServiceAreaSelector = () => {
    if (!this.props.category) {
      return (
        <ServiceAreaSelector
          searchState={this.state.searchState}
          executeNewSearch={this.executeNewSearch}
        />
      )
    }
  }

  renderSearchFilters = () => {
    return (
      <SearchFilters
        filterResults={this.state.filterResults}
        selectedCommodityCodeSegmentId={this.state.selectedCommodityCodeSegmentId}
        selectedCommodityCodeSegmentTitle={this.state.selectedCommodityCodeSegmentTitle}
        activeCommodityFamilies={this.state.activeCommodityFamilies}
        searchState={this.state.searchState}
        categoryIds={this.state.categoryIds}
        designations={this.state.designations}
        seg={this.state.seg}
        commodityCodes={this.state.commodityCodes}
        minRating={this.state.minRating}
        onFilterClear={this.handleFilterClear}
        onCategoriesChange={this.handleCategoriesChange}
        onDesignationsChange={this.handleDesignationsChange}
        onSegChange={this.handleSegChange}
        onCommodityCodeChange={this.handleCommodityCodeChange}
        onCommodityCodeSegmentChange={this.handleCommodityCodeSegmentChange}
        onMinRatingChange={this.handleMinRatingChange}
        onLockedClick={this.handleLockedClick}
        onCloseMobileFilter={this.handleCloseMobileFilter}
        locked={this.props.locked}
        showCategories={this.props.showCategories}
        showUnpublishedCategories={this.props.showUnpublishedCategories}
        category={this.props.category}
      />
    )
  }

  renderSearchForm = () => {
    if (!this.props.category) {
      return (
        <div className='large-12 cell' id='search-field'>
          <SearchForm
            searchTerm={this.state.searchTerm}
            executeNewSearch={this.executeNewSearch}
          />
        </div>
      )
    }
  }

  renderSearchSummary = () => {
    if (!this.props.category) {
      return (
        <div className={this.state.searching ? 'hide' : ''}>
          <SearchSummary
            totalCount={this.state.searchResults.total_count}
            searchTerm={this.state.searchTerm}
            filterCount={this.filterCount()}
          />
        </div>
      )
    }
  }

  render () {
    return (
      <StatesContext.Provider value={this.props.states}>
        {this.renderServiceAreaSelector()}
        <div className='grid-x grid-margin-x'>
          <div className='large-4 medium-4 cell search-filters desktop'>
            {this.renderSearchFilters()}

            <CategoryAd ad={this.props.adSideRail} />
          </div>

          <div
            className='reveal'
            id='mobile-filters-modal'
            data-reveal
            ref={this.mobileFilterModalRef}
          >
            <div className='search-filters'>
              {this.renderSearchFilters()}
            </div>

          </div>

          <div className='large-8 medium-8 cell'>
            <div className='grid-x'>
              {this.renderSearchForm()}
              <div className='large-12 cell'>
                <div className='grid-x'>
                  <div className='cell text-center'>
                    <LoadingDots show={this.state.searching} />
                  </div>
                </div>
              </div>

              {this.renderSearchSummary()}

              <div className='cell larger-12 mobile-filter-button padding-bottom-3'>
                <button
                  type='button'
                  className='button'
                  onClick={this.handleOpenMobileFilters}
                >
                  Filter
                  &nbsp;
                  <i className='fad fa-filter icon' />
                </button>
              </div>

              <div className='large-12 cell'>
                <div className={`grid-x ${this.state.searching ? 'hide' : ''}`}>
                  <SearchResults
                    searchResults={this.state.searchResults}
                    searchId={this.state.searchId}
                    popularSearches={this.props.popularSearches}
                    executeSearch={this.executeSearch}
                    category={this.currentCategory()}
                    onLockedClick={this.handleLockedClick}
                    locked={this.props.locked}
                    linkToPublicProfile={!this.props.userIsAuthenticated}
                    sponsoredSearchResult={this.sponsoredSearchResult()}
                    mobileAd={<CategoryAd ad={this.props.adSideRail} variant='mobile' shouldTrackView={false} />}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </StatesContext.Provider>
    )
  }
}

Page.propTypes = {
  searchResults: PropTypes.shape({
    current_page: PropTypes.number.isRequired,
    total_count: PropTypes.number.isRequired,
    total_pages: PropTypes.number.isRequired,
    results: PropTypes.array.isRequired
  }),
  filterResults: PropTypes.shape({
    active_segment: PropTypes.object,
    active_designations: PropTypes.object.isRequired,
    all_categories: PropTypes.array.isRequired,
    all_designations: PropTypes.array.isRequired,
    commodity_code_families: PropTypes.array.isRequired,
    commodity_code_segments: PropTypes.object.isRequired,
    designations_by_state: PropTypes.object.isRequired
  }),
  queryParams: PropTypes.object.isRequired,
  searchId: PropTypes.number,
  popularSearches: PropTypes.array.isRequired,
  locked: PropTypes.bool.isRequired,
  states: PropTypes.array.isRequired,
  category: PropTypes.object,
  showCategories: PropTypes.bool.isRequired,
  showUnpublishedCategories: PropTypes.bool.isRequired,
  userIsAuthenticated: PropTypes.bool,
  sponsoredSearchResults: PropTypes.objectOf(PropTypes.arrayOf(SupplierResultShape)),
  signedIn: PropTypes.bool
}

export default Page
